import { Button, ButtonBase, DialogActions } from "@mui/material";
import { motion } from "framer-motion";
import { createElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons/lib";
import { TbBriefcase, TbCalendarOff, TbHelpHexagon } from "react-icons/tb";

import CommonPopup from "@/components/CommonPopup";
import { useShiftActions, useShiftShowShiftDecision } from "@/stores/use-shift";

// Animation Variants
const iconVariants = {
    initial: { opacity: 0, rotateY: 90 },
    animate: { opacity: 1, rotateY: 0 },
};

const textVariants = {
    initial: { opacity: 0, rotateX: 90 },
    animate: { opacity: 1, rotateX: 0 },
};

const cardVariants = {
    initial: { opacity: 0, x: "100%" },
    animate: (delay: number) => ({
        opacity: 1,
        x: 0,
        transition: { delay, duration: 0.4 },
    }),
    hover: {
        translateX: -2,
        translateY: -5,
    },
};

export function ChooseShiftElements({
    handleStartShift,
    handleFreeDay,
}: {
    handleStartShift: (val?: boolean) => void;
    handleFreeDay: (val?: boolean) => void;
}) {
    const items: { icon: IconType; title: string; description: string; handleClick: () => void }[] = useMemo(
        () => [
            {
                icon: TbBriefcase,
                title: "Evet",
                description: "Mesainizi başlatmak için tıklayınız. Mesainizi en az 1 saat sonra bitirebilirsiniz.",
                handleClick: () => handleStartShift(true),
            },
            {
                icon: TbCalendarOff,
                title: "Hayır",
                description: "Haftalık tatilinizi bildirmek için tıklayınız.",
                handleClick: () => handleFreeDay(true),
            },
        ],
        [handleStartShift, handleFreeDay]
    );

    return items.map((item, index) => <CardItem key={index} custom={+(+`0.${index}` + 0.4)} {...item} />);
}

function ChooseShift({
    handleStartShift,
    handleFreeDay,
}: {
    handleStartShift: (val?: boolean) => void;
    handleFreeDay: (val?: boolean) => void;
}) {
    const showShiftDecision = useShiftShowShiftDecision();
    const { updateShiftDecision } = useShiftActions();

    const { t } = useTranslation();
    const [num, setNum] = useState(false);

    const handleClose = () => {
        updateShiftDecision();
    };

    return (
        <CommonPopup title={""} icon={false} open={showShiftDecision} size="sm" onClose={handleClose}>
            <>
                <div className="flex justify-center">
                    <motion.div
                        variants={iconVariants}
                        initial="initial"
                        animate="animate"
                        transition={{ delay: 0.2, duration: 0.4 }}
                        onClick={() => setNum(!num)}
                        className="mx-auto flex w-14 h-14 animate-pulse bg-blue-100 text-blue-800 shrink-0 items-center justify-center rounded-full cursor-pointer"
                    >
                        <TbHelpHexagon fontSize={25} />
                    </motion.div>
                </div>

                <motion.div
                    key={num.toString()}
                    variants={textVariants}
                    initial="initial"
                    animate="animate"
                    transition={{ delay: 0.2, duration: 0.4 }}
                    className="text-center font-semibold text-lg mt-4"
                >
                    Bugün Çalışıyor musunuz?
                </motion.div>

                <div className="flex flex-col gap-3 lg:gap-6 px-4  mt-2 py-2 overflow-hidden max-w-full">
                    <ChooseShiftElements handleStartShift={handleStartShift} handleFreeDay={handleFreeDay} />
                </div>

                <DialogActions>
                    <Button color="secondary" variant="outlined" onClick={handleClose}>
                        {t("buttons.close")}
                    </Button>
                </DialogActions>
            </>
        </CommonPopup>
    );
}

export function CardItem({
    children,
    custom,
    icon,
    title,
    description,
    handleClick,
}: {
    icon: IconType;
    custom: number;
    title: string;
    description: string;
    handleClick: () => void;
    children?: React.ReactNode;
}) {
    return (
        <ButtonBase component="div" onClick={handleClick}>
            <motion.div
                custom={custom}
                variants={cardVariants}
                whileHover="hover"
                initial="initial"
                animate="animate"
                className="flex active:bg-gray-100  bg-gradient-to-br from-slate-200 to-slate-50 active:scale-95 flex-col relative gap-2 w-full min-h-[90px] border rounded-lg shadow p-3"
            >
                <div className="absolute right-0 top-0 m-3">{createElement(icon, { color: "gray" })}</div>
                <div className="font-semibold">{title}</div>
                <span className="text-xs" dangerouslySetInnerHTML={{ __html: description }}></span>
                {children}
            </motion.div>
        </ButtonBase>
    );
}

export default ChooseShift;
